import { useQuery } from '@tanstack/react-query';
import { getRecipeFonts, type WorkspaceCustomFontDto } from '@/api/recipes';

/** Invalidate on sign-out via `queryClient.removeQueries({ queryKey: RECIPE_FONTS_QUERY_KEY_ROOT })`. */
export const RECIPE_FONTS_QUERY_KEY_ROOT = ['recipe-fonts'] as const;

export function buildWorkspaceCustomFontsUrlsMap(fonts: WorkspaceCustomFontDto[]): ReadonlyMap<string, string> {
  const m = new Map<string, string>();
  for (const f of fonts) {
    if (f.originalFileName) {
      m.set(f.originalFileName, f.url);
    }
  }
  return m;
}

type useWorkflowFontsQueryOptions = {
  enabled: boolean;
};

/**
 * GET `/v1/recipes/{recipeId}/fonts`. Disabled when `recipeId` is missing or `enabled` is false.
 *
 * Cached aggressively (no background refetch): the workflow graph depends on the derived URL map, so refetches would recreate the graph and clear editor state.
 */
export function useWorkflowFontsQuery(recipeId: string | undefined, options: useWorkflowFontsQueryOptions) {
  return useQuery({
    queryKey: [...RECIPE_FONTS_QUERY_KEY_ROOT, recipeId],
    queryFn: () => getRecipeFonts(recipeId!),
    enabled: Boolean(recipeId) && options.enabled,
    retry: false,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
